import React, { useState } from "react";
import { Header } from "../../components/Header/Headers";
import ManagersList from "./Components/ManagersList";
import AddNewManager, {
  AddNewManagerFormData,
} from "./Components/AddNewManager";
import api from "../../api/axiosConfig";
import { endpoints } from "../../api/endpoints";

interface EditManagerData {
  id: string;
  fullName: string;
  email: string;
  phone: string;
  stationType: string;
}

const UsersAndRolesSection: React.FC = () => {
  const [addUserModalOpen, setAddUserModalOpen] = useState(false);
  const [isSubmittingManager, setIsSubmittingManager] = useState(false);
  const [managerError, setManagerError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [editManager, setEditManager] = useState<EditManagerData | null>(null);

  const getStationTypePayload = (stationType: string) => {
    return stationType === "Filling" ? "fuel station" : "testing center";
  };

  const getStationTypeUpdatePayload = (stationType: string) => {
    return stationType === "Filling" ? "FUEL_STATION" : "TESTING_CENTER";
  };

  const handleAddUserClick = () => {
    setManagerError(null);
    setEditManager(null);
    setAddUserModalOpen(true);
  };

  const handleEditManager = (user: {
    id: string;
    name: string;
    email: string;
    phone: string;
    stationType: string;
  }) => {
    console.log("Editing manager:", user);
    setManagerError(null);
    setEditManager({
      id: user.id,
      fullName: user.name,
      email: user.email,
      phone: user.phone === "--" ? "" : user.phone,
      stationType:
        user.stationType === "Fuel Station" || user.stationType === "Filling"
          ? "Filling"
          : "Testing",
    });
    setAddUserModalOpen(true);
  };

  const handleCloseModal = () => {
    setManagerError(null);
    setEditManager(null);
    setAddUserModalOpen(false);
  };

  const handleSaveUser = async (data: AddNewManagerFormData) => {
    setManagerError(null);
    setIsSubmittingManager(true);

    try {
      if (editManager) {
        const payload: Record<string, string> = {
          name: data.fullName.trim(),
          email: data.email.trim(),
          station_type: getStationTypeUpdatePayload(data.stationType),
        };

        if (data.phone.trim()) {
          payload.phone = data.phone.trim();
        }

        if (data.userPassword.trim()) {
          payload.password = data.userPassword;
        }

        await api.patch(endpoints.users.updateManager(editManager.id), payload);
      } else {
        const payload: Record<string, string> = {
          name: data.fullName.trim(),
          email: data.email.trim(),
          password: data.userPassword.trim(),
          station_type: getStationTypePayload(data.stationType),
        };

        if (data.phone.trim()) {
          payload.phone = data.phone.trim();
        }

        await api.post(endpoints.users.createManager, payload);
      }

      setRefreshKey((current) => current + 1);
      setEditManager(null);
      setAddUserModalOpen(false);
      return true;
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.detail?.message ||
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        (editManager
          ? "Failed to update manager. Please try again."
          : "Failed to create manager. Please try again.");

      setManagerError(errorMessage);
      return false;
    } finally {
      setIsSubmittingManager(false);
    }
  };

  return (
    <>
      <Header
        title="Users & Roles"
        breadcrumb="Admin Console / Users & Roles"
        isUserPage={true}
        handleAddUserClick={handleAddUserClick}
      />
      <ManagersList refreshKey={refreshKey} onEditManager={handleEditManager} />
      <AddNewManager
        open={addUserModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveUser}
        isSubmitting={isSubmittingManager}
        errorMessage={managerError}
        onClearError={() => setManagerError(null)}
        initialData={editManager ?? undefined}
        isEditMode={!!editManager}
      />
    </>
  );
};

export default UsersAndRolesSection;
